import React, { useState, useEffect, useCallback } from 'react';
import { Checkbox, Button, Space, Typography, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faTimes,
  faSearch,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import API, { getMediaURL } from '../../../context/API';
import type { AppDispatch, RootState } from '../../../redux/store';
import { setCategoryItems as setCategoryItemsRedux } from '../../../redux/Menu';
import { useTheme } from '../../../context/ThemeContext';
import {
  StyledCard,
  StyledButton,
  ItemCard,
  ScrollableContainer,
  LoadingSpinnerContainer,
  EmptyStateContainer,
  FullWidthSpace,
  CursorPointerText,
  TitleNoMargin,
  SearchIcon,
  InputMarginBottom,
  FullWidthSpaceSmall,
  FlexSpace,
  SecondaryTextSmall,
  StyledImage,
} from './Styles';
import { AddToItemsProps, Category, CategoryItem, RelatedItem } from './types';
import { API_ENDPOINTS, SUBMISSION_TYPES, MESSAGES, PLACEHOLDER_IMAGES } from './constants';

const { Text } = Typography;

const AddToItems: React.FC<AddToItemsProps> = ({
  customizationData,
  handleBackClick,
  handleSubmit,
  handleRelatedItemsChange,
  width,
}) => {
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const [relatedItems, setRelatedItems] = useState<RelatedItem[]>(
    (customizationData.related_items || []).map((relatedItem) => {
      const itemValue = typeof relatedItem.item === 'string' 
        ? relatedItem.item 
        : (relatedItem.item as any)?.id 
          ? { id: (relatedItem.item as any).id, item: (relatedItem.item as any).item }
          : relatedItem.item;
      
      return {
        id: typeof relatedItem.item === 'object' && relatedItem.item !== null && 'id' in relatedItem.item
          ? (relatedItem.item as any).id
          : relatedItem.id,
        item: itemValue,
        sequence_number: relatedItem.sequence_number || 0,
      };
    })
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [categoryItems, setCategoryItems] = useState<CategoryItem[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get categories from Redux store
  const menuCategories = useSelector((state: RootState) => state.menu.categories);
  const categoryItemsMap = useSelector((state: RootState) => state.menu.categoryItemsMap);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await API.get(API_ENDPOINTS.CATEGORIES);
        if (response.data.success) {
          const categoriesData = response.data.data.map((category: Category) => ({
            ...category,
            picture: getMediaURL(category.picture),
          }));
          setCategories(categoriesData);
          setFilteredCategories(categoriesData);
        }
      } catch (error) {
        // Error fetching categories
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Use Redux categories if available
  useEffect(() => {
    if (menuCategories.length > 0) {
      setLoadingCategories(false);
      const categoriesData = menuCategories.map((category) => ({
        id: category.id,
        category: category.category,
        picture: category.picture,
      }));
      setCategories(categoriesData);
      setFilteredCategories(categoriesData);
    }
  }, [menuCategories]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value.toLowerCase();
      setSearchQuery(query);

      const filtered = categories.filter((category) =>
        category.category.toLowerCase().includes(query)
      );
      setFilteredCategories(filtered);

      setIsSearching(!!query);
    },
    [categories]
  );

  const handleSearchFocus = useCallback(() => {
    setIsSearching(true);
    setSelectedCategory(null);
  }, []);

  // Show categories by default when component loads (if no related items and categories are loaded)
  useEffect(() => {
    if (relatedItems.length === 0 && !selectedCategory && categories.length > 0 && !isSearching && !searchQuery) {
      // Show categories by default when first opening
      setIsSearching(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatedItems.length, selectedCategory, categories.length]);

  // const handleBack = useCallback(() => {
  //   if (selectedCategory) {
  //     setSelectedCategory(null);
  //     setCategoryItems([]);
  //   } else {
  //     setIsSearching(false);
  //     setSearchQuery('');
  //   }
  // }, [selectedCategory]);

  const handleCategoryClick = useCallback(
    async (categoryId: number) => {
      try {
        setLoadingItems(true);
        // Try to get from Redux first
        let itemsData: CategoryItem[] = [];
        
        if (categoryItemsMap[categoryId]) {
          itemsData = categoryItemsMap[categoryId].map((item) => ({
            id: item.id,
            item: item.item,
            picture: getMediaURL(item.picture),
            price: item.price,
            isChecked: relatedItems.some(
              (relatedItem) => {
                const itemId = typeof relatedItem.item === 'object' && relatedItem.item !== null && 'id' in relatedItem.item
                  ? (relatedItem.item as any).id
                  : null;
                return relatedItem.id === item.id || itemId === item.id;
              }
            ),
          }));
        } else {
          // Fallback to API call
          const response = await API.get(API_ENDPOINTS.CATEGORY_ITEMS(categoryId));
          if (response.data.success) {
            itemsData = response.data.data.map((item: any) => ({
              id: item.id,
              item: item.item,
              picture: getMediaURL(item.picture),
              price: item.price,
              isChecked: relatedItems.some(
                (relatedItem) => {
                  const itemId = typeof relatedItem.item === 'object' && relatedItem.item !== null && 'id' in relatedItem.item
                    ? (relatedItem.item as any).id
                    : null;
                  return relatedItem.id === item.id || itemId === item.id;
                }
              ),
            }));
            
            // Update Redux store
            (dispatch as any)(setCategoryItemsRedux({ categoryId, items: response.data.data }));
          }
        }
        
        setCategoryItems(itemsData);
        setSelectedCategory(categoryId);
      } catch (error) {
        // Error fetching category items
      } finally {
        setLoadingItems(false);
      }
    },
    [relatedItems, categoryItemsMap, dispatch]
  );

  const handleRemoveItem = useCallback(
    (itemId: number) => {
      const updatedItems = relatedItems.filter(
        (item) => (item.item as any)?.id !== itemId
      );
      setRelatedItems(updatedItems);
      handleRelatedItemsChange(updatedItems);
    },
    [relatedItems, handleRelatedItemsChange]
  );

  const handleCheckboxChange = useCallback(
    (item: CategoryItem) => {
      let updatedItems: RelatedItem[];
      const isCurrentlySelected = relatedItems.some(
        (relatedItem) => relatedItem.id === item.id || (relatedItem.item as any)?.id === item.id
      );

      if (isCurrentlySelected) {
        updatedItems = relatedItems.filter(
          (relatedItem) =>
            relatedItem.id !== item.id && (relatedItem.item as any)?.id !== item.id
        );
      } else {
        const newRelatedItem: RelatedItem = {
          id: item.id,
          item: item,
          sequence_number: relatedItems.length,
        };
        updatedItems = [...relatedItems, newRelatedItem];
      }

      const updatedCategoryItems = categoryItems.map((categoryItem) =>
        categoryItem.id === item.id
          ? { ...categoryItem, isChecked: !isCurrentlySelected }
          : categoryItem
      );
      setCategoryItems(updatedCategoryItems);
      setRelatedItems(updatedItems);
      handleRelatedItemsChange(updatedItems);
    },
    [relatedItems, categoryItems, handleRelatedItemsChange]
  );

  const saveChanges = useCallback(async () => {
    setIsSaving(true);
    try {
      const updatedCustomizationData = {
        ...customizationData,
        related_items: relatedItems,
      };

      await handleSubmit(SUBMISSION_TYPES.ADD_TO_ITEMS, updatedCustomizationData);
    } finally {
      setIsSaving(false);
    }
  }, [customizationData, relatedItems, handleSubmit]);

  const isFixed = !width;

  return (
    <StyledCard $width={width} $isFixed={isFixed}>
      <FullWidthSpace direction="vertical" size="middle">
        <Space>
          <Button
            type="text"
            icon={<FontAwesomeIcon icon={faChevronLeft} />}
            onClick={handleBackClick}
            size="small"
          />
          <CursorPointerText onClick={handleBackClick}>
            Customizations
          </CursorPointerText>
          <Text> / Add to items</Text>
        </Space>

        <TitleNoMargin level={4}>Add to Items</TitleNoMargin>
        <Text type="secondary">Select which items these customizations will be added to.</Text>
        
        <InputMarginBottom
          placeholder="Search all items or categories"
          prefix={
            <SearchIcon>
              <FontAwesomeIcon icon={faSearch} />
            </SearchIcon>
          }
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
        />

        <ScrollableContainer>
          {loadingCategories ? (
            <LoadingSpinnerContainer>
              <Spin size="large" tip={MESSAGES.LOADING_CATEGORIES} />
            </LoadingSpinnerContainer>
          ) : selectedCategory ? (
            loadingItems ? (
              <LoadingSpinnerContainer>
                <Spin size="large" tip={MESSAGES.LOADING_ITEMS} />
              </LoadingSpinnerContainer>
            ) : categoryItems.length > 0 ? (
              <FullWidthSpaceSmall direction="vertical" size="small">
                {categoryItems.map((item) => (
                  <ItemCard key={item.id} $isDark={isDarkMode}>
                    <Space>
                      <Checkbox
                        checked={item.isChecked}
                        onChange={() => handleCheckboxChange(item)}
                      />
                      <StyledImage
                        width={50}
                        height={50}
                        src={item.picture}
                        alt={item.item}
                        fallback={PLACEHOLDER_IMAGES.ITEM}
                      />
                      <div>
                        <Text strong>{item.item}</Text>
                        <div>
                          <SecondaryTextSmall type="secondary">
                            ${item.price != null ? (item.price || 0).toFixed(2) : ''}
                          </SecondaryTextSmall>
                        </div>
                      </div>
                    </Space>
                  </ItemCard>
                ))}
              </FullWidthSpaceSmall>
            ) : (
              <EmptyStateContainer>
                <Text type="secondary">{MESSAGES.NO_ITEMS_FOUND}</Text>
              </EmptyStateContainer>
            )
          ) : (isSearching || relatedItems.length === 0) ? (
            filteredCategories.length > 0 ? (
              <FullWidthSpaceSmall direction="vertical" size="small">
                {filteredCategories.map((category) => (
                  <ItemCard
                    key={category.id}
                    $isDark={isDarkMode}
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    <FlexSpace>
                      <TitleNoMargin level={5}>{category.category}</TitleNoMargin>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </FlexSpace>
                  </ItemCard>
                ))}
              </FullWidthSpaceSmall>
            ) : (
              <EmptyStateContainer>
                <Text type="secondary">
                  {searchQuery ? MESSAGES.NO_CATEGORIES_MATCHING_SEARCH : MESSAGES.NO_CATEGORIES_AVAILABLE}
                </Text>
              </EmptyStateContainer>
            )
          ) : (
            <FullWidthSpaceSmall direction="vertical" size="small">
              {relatedItems.map((relatedItem) => {
                const { item } = relatedItem;
                const hasItem = item && (item as any).id;

                return (
                  <ItemCard key={relatedItem.id} $isDark={isDarkMode}>
                    <FlexSpace>
                      <Space>
                        {hasItem ? (
                          <>
                            <StyledImage
                              width={50}
                              height={50}
                              src={getMediaURL((item as any).picture) || PLACEHOLDER_IMAGES.ITEM}
                              alt={(item as any).item || 'Unnamed Item'}
                              fallback={PLACEHOLDER_IMAGES.ITEM}
                            />
                            <div>
                              <Text strong>{(item as any).item || 'Unnamed Item'}</Text>
                              <div>
                                <SecondaryTextSmall type="secondary">
                                  {(item as any).price != null
                                    ? `$${((item as any).price || 0).toFixed(2)}`
                                    : 'Price not available'}
                                </SecondaryTextSmall>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <StyledImage
                              width={50}
                              height={50}
                              src={PLACEHOLDER_IMAGES.ITEM}
                              alt="Unnamed Item"
                              fallback={PLACEHOLDER_IMAGES.ITEM}
                            />
                            <div>
                              <Text strong>Unnamed Item</Text>
                              <div>
                                <SecondaryTextSmall type="secondary">
                                  Price not available
                                </SecondaryTextSmall>
                              </div>
                            </div>
                          </>
                        )}
                      </Space>
                      {hasItem && (
                        <Button
                          type="text"
                          danger
                          icon={<FontAwesomeIcon icon={faTimes} />}
                          onClick={() => handleRemoveItem((item as any).id)}
                          size="small"
                        />
                      )}
                    </FlexSpace>
                  </ItemCard>
                );
              })}
            </FullWidthSpaceSmall>
          )}
        </ScrollableContainer>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
          <StyledButton 
            onClick={saveChanges}
            loading={isSaving}
            icon={isSaving ? <Spin size="small" /> : null}
            style={{ width: 'auto' }}
          >
            Save
          </StyledButton>
        </div>
      </FullWidthSpace>
    </StyledCard>
  );
};

export default React.memo(AddToItems);
